import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  FiatCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import common from './assets/common.webp';
import limited from './assets/limited.webp';
import rare from './assets/rare.webp';
import superRare from './assets/super_rare.webp';
import unique from './assets/unique.webp';

const Root = styled(Vertical)`
  padding: var(--triple-unit);
  position: relative;

  background-image: var(--bg);
  background-size: cover;
  background-position: center;
  border-radius: var(--unit);

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--c-nd-50);
    border-radius: var(--unit);
  }
`;
const Content = styled.div`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  align-items: end;
  gap: var(--half-unit);
  @media ${tabletAndAbove} {
    flex-direction: row;
    align-items: start;
    gap: var(--unit);
  }
`;
const Title = styled.p`
  font: var(--t-48);
  line-height: 1;
  text-shadow: 0px 4px 4px rgba(var(--c-rgb-black), 0.25);

  &.highContrast {
    text-shadow: 0px 4px 4px var(--c-black);
  }
`;

const isHighContrast = (rarity: Rarity) => rarity === Rarity.common;

const backgrounds = {
  [Rarity.common]: common,
  [Rarity.limited]: limited,
  [Rarity.rare]: rare,
  [Rarity.super_rare]: superRare,
  [Rarity.unique]: unique,
};

type Props = {
  amounts: { cashPrize: number; goldenGoal?: number };
  rarity: Rarity;
};

export const CashPrize = ({ amounts, rarity }: Props) => {
  const pictureUrl =
    rarity in backgrounds
      ? backgrounds[rarity as keyof typeof backgrounds]
      : null;

  if (!pictureUrl) {
    return null;
  }

  return (
    <Root style={{ '--bg': `url(${pictureUrl})` } as CSSProperties}>
      {!!amounts.cashPrize && (
        <Content>
          <Title
            className={classNames({ highContrast: isHighContrast(rarity) })}
          >
            <FormattedNumber
              value={amounts.cashPrize}
              currency={FiatCurrency.USD}
              style="currency"
              maximumFractionDigits={0}
            />
          </Title>
          <Chip color={isHighContrast(rarity) ? 'white' : 'transparent'}>
            <BrandXXS as="p">
              <FormattedMessage defaultMessage="Cash Prizes" />
            </BrandXXS>
          </Chip>
        </Content>
      )}
      {!!amounts.goldenGoal && (
        <Content>
          <Title
            className={classNames({ highContrast: isHighContrast(rarity) })}
          >
            <FormattedNumber
              value={amounts.goldenGoal}
              currency={FiatCurrency.USD}
              style="currency"
              maximumFractionDigits={0}
            />
          </Title>
          <Chip color={isHighContrast(rarity) ? 'white' : 'transparent'}>
            <BrandXXS as="p">
              <FormattedMessage defaultMessage="Golden Goal" />
            </BrandXXS>
          </Chip>
        </Content>
      )}
    </Root>
  );
};
